import type {
  AdminCatalogQuery,
  CreateCatalogItemRequest,
  CustomNamesQuery,
  PromoteCustomNameRequest,
  UpdateCatalogItemRequest,
} from '@lista/shared-validation';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCore } from '../context';
import { queryKeys } from '../query-keys';

/**
 * Catalog administration (permission `catalog.manage`, ADR-0023). A 403 simply means the
 * account isn't a catalog admin: callers use `isSuccess` of useCatalogAdminAccess to show the
 * entry point.
 */
export function useCatalogAdminAccess() {
  const { services } = useCore();
  return useQuery({
    queryKey: [...queryKeys.catalogAdmin, 'access'],
    queryFn: () => services.catalogAdmin.items({ status: 'all', pageSize: 1 }),
    retry: false,
    staleTime: 5 * 60_000,
  });
}

/** One numbered page; the previous page stays on screen while the next one loads. */
export function useAdminCatalogItems(query: Partial<AdminCatalogQuery>) {
  const { services } = useCore();
  return useQuery({
    queryKey: [...queryKeys.catalogAdmin, 'items', query],
    queryFn: () => services.catalogAdmin.items(query),
    placeholderData: keepPreviousData,
  });
}

/** Names users typed for their own items ("Suggested by users"), one numbered page. */
export function useCustomNames(query: Partial<CustomNamesQuery>) {
  const { services } = useCore();
  return useQuery({
    queryKey: [...queryKeys.catalogAdmin, 'custom-names', query],
    queryFn: () => services.catalogAdmin.customNames(query),
    placeholderData: keepPreviousData,
  });
}

function useInvalidateCatalog() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.catalogAdmin });
    // Users' views change too: browse, search and recents (all under 'catalog'), and the
    // names on lists and favorites after a promotion merges their custom items.
    void qc.invalidateQueries({ queryKey: ['catalog'] });
    void qc.invalidateQueries({ queryKey: queryKeys.lists.all });
    void qc.invalidateQueries({ queryKey: queryKeys.favorites });
  };
}

export function useCreateCatalogItem() {
  const { services } = useCore();
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: (body: CreateCatalogItemRequest) => services.catalogAdmin.create(body),
    onSuccess: invalidate,
  });
}

export function useUpdateCatalogItem() {
  const { services } = useCore();
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCatalogItemRequest }) =>
      services.catalogAdmin.update(id, body),
    onSuccess: invalidate,
  });
}

export function usePromoteCustomName() {
  const { services } = useCore();
  const invalidate = useInvalidateCatalog();
  return useMutation({
    mutationFn: (body: PromoteCustomNameRequest) => services.catalogAdmin.promote(body),
    onSuccess: invalidate,
  });
}
